import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/cms/client/generated-form.js';
import { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { shouldIncludeFieldHelpers } from '@adornis/field-management/field-management-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { ListAspectSchema } from '@adornis/filter/aspect-schemas/ListAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { FilterEditor } from '@adornis/filter/client/filter-editor.js';
import { subscribePaginatedAndFiltered } from '@adornis/filter/db/subscribePaginatedAndFiltered.js';
import { getStandardFilterOperation } from '@adornis/filter/get-standard-filter-operation.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { buildChancenDataViewOptions } from '@chancenwerk/client/design/components/cr-data-view.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import '@chancenwerk/client/components/chancen-component-card-title.js';
import '@chancenwerk/client/components/chancen-component-context-menu.js';
import { projectConfig } from '@chancenwerk/config/config.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-date-picker-dropdown.js';
import { CrPage } from '@chancenwerk/client/design/components/cr-page.js';
import { FamilyEvent } from '@chancenwerk/family/entities/FamilyEvent.js';
import { FamilyRegistration } from '@chancenwerk/family/entities/FamilyRegistration.js';
import { ChancenPersonalConfigAPI } from '@chancenwerk/shared/user-configs/api/ChancenPersonalConfigAPI.js';
import { ListUIConfig } from '@chancenwerk/shared/user-configs/entities/ListUIConfig.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { toCanvas } from 'qrcode';
import { switchMap } from 'rxjs';

const filterSelector: BaseQLSelectionSet<AdornisFilter> = {
  ...AdornisFilter.allFields,
  aspects: { ...AbstractAspect.allFields, value: 1, zipCity: { zip: 1, city: 1 } },
};

/**
 * @customElement family-page-event
 */
@customElement('family-page-event')
export class FamilyPageEvent extends CrPage {
  @property({ attribute: false }) public eventID = new RXController(this, null as Maybe<string>);

  @state() _event = new RXController(
    this,
    this.eventID.observable.pipe(
      switchMap(id => {
        return subscribeByID(FamilyEvent, id ?? '')({ name: 1, cameCount: 1, publicName: 1, when: 1 });
      }),
    ),
  );

  @state() _view = new View({ filter: new AdornisFilter({ aspects: [] }) });

  @state() private readonly _config = new RXController(
    this,
    ChancenPersonalConfigAPI.getByKeyAndCategory(
      ListUIConfig<typeof FamilyRegistration>,
      'list-family-registrations',
      'default',
    )({
      key: 1,
      userID: 1,
      category: 1,
      view: {
        filter: filterSelector,
        mode: 1,
        configuredColumns: { customID: 1, fieldName: 1, columnWidth: 1 },
        selectedItems: 1,
        multiSelection: 1,
      },
    }),
  );

  constructor() {
    super();

    this.label = 'Event';
    this.description = 'Übersicht über diesen Termin';
    this.permission = ChancenPermission.family.LOGIN_FAMILY;
    this.backRoute = '/';
  }
  private _getURL(): string {
    return `${projectConfig.get('ANMELDUNG_URL')}chancenfamily/${this.eventID.value}`;
  }

  override updated(prop: any) {
    super.updated(prop);

    const canvas = this.shadowRoot?.getElementById('canvas');
    if (canvas) {
      toCanvas(canvas, this._getURL(), { width: 270 }, error => {
        if (error) console.error(error);
      });
    }
  }

  override header() {
    return html`<chancen-component-context-menu
      .items=${[
        {
          icon: 'edit',
          label: 'Bearbeiten',
          action: async () => {
            const res = await ChromiumDialog.showPopup<boolean>({
              content: resolve => {
                const form = new GenericFieldFormController(this, this._event.observable, {
                  onSuccess: async doc => {
                    await doc.save();
                    resolve(true);
                  },
                });

                return html`<cr-flex ${css({ width: '400px' })} padding="md" space="md">
                  <cr-input placeholder="Name" ${form.field('name')}></cr-input>
                  <cr-input placeholder="öffentlicher Name" ${form.field('publicName')}></cr-input>
                  <cr-date-picker-dropdown
                    .end-year=${DateTime.now().plus({ years: 5 }).year}
                    placeholder="wann?"
                    click-to-save
                    flex
                    clearable
                    show-time
                    ${form.field('when')}
                  ></cr-date-picker-dropdown>
                  <cr-button @click=${() => form.validate()}>Speichern</cr-button>
                </cr-flex>`;
              },
            });
            if (!res) {
              this._event.value?.revert();
            }
          },
        },
        {
          icon: 'delete',
          label: 'Löschen',
          action: async () => {
            ChromiumDialog.guard(
              async () => {
                await this._event.value?.remove();
                goTo('/');
              },
              { hasToConfirm: true, confirmText: 'Event wirklich löschen?' },
            );
          },
        },
      ]}
    ></chancen-component-context-menu>`;
  }

  override content() {
    return html`<cr-grid columns="400px 1fr" padding="sm" space="md" ${css({ alignItems: 'start' })}>
      <cr-card padding="xl" space="lg">
        <cr-grid columns="150px 1fr" space="md">
          <cr-text bold>Name:</cr-text>
          <cr-text>${this._event.value?.name}</cr-text>
          <cr-text bold>öffentlicher Name:</cr-text>
          <cr-text>${this._event.value?.publicName}</cr-text>
          <cr-text bold>Wann:</cr-text>
          <cr-text>${this._event.value?.when?.toFormat('dd.MM.yyyy, HH:mm')}</cr-text>
        </cr-grid>
        <cr-hr></cr-hr>
        <cr-input
          .value=${this._event.value?.cameCount}
          type="number"
          @value-picked=${async (e: ValueEvent<Maybe<number>>) => {
            if (!this._event.value) return;
            this._event.value.cameCount = e.detail.value;
            await this._event.value?.save();
          }}
          placeholder="Anzahl Teilnahmen"
          icon="save"
          @icon-click=${async () => {
            await this._event.value?.save();
            ChromiumSnackbar.show('Anzahl aktualisiert');
          }}
        ></cr-input>
        <cr-hr></cr-hr>
        <cr-flex crossaxis-center space="lg">
          <canvas id="canvas"></canvas>
          <cr-input
            ${css({ width: '100%' })}
            placeholder="Anmelde-URL"
            .value=${this._getURL()}
            readonly
            icon="content_copy"
            @click=${async () => {
              navigator.clipboard.writeText(this._getURL());
              await ChromiumSnackbar.show('Link in die Zwischenablage kopiert');
            }}
          ></cr-input>
        </cr-flex>
      </cr-card>
      ${this._registration()}
    </cr-grid>`;
  }

  private _registration() {
    if (this._config.isLoading || !this._event.value) {
      return html`<cr-loader></cr-loader>`;
    }

    const view = this._config.value?.view ?? new View({ filter: new AdornisFilter({}) });
    const options = buildChancenDataViewOptions<typeof FamilyRegistration>(
      {
        class: FamilyRegistration,
        itemSpacing: this.spacing.md,
        defaultFieldMapping: { DateTime: (value: DateTime) => value.toFormat('dd.MM.yyyy') },
        view: view,
        operation: (skip, limit, filter) => {
          const changedFilter = new AdornisFilter({ ...filter.toObject() });

          changedFilter.aspects.push(
            new StringAspect({ fieldName: 'eventID', mode: 'equals', value: this.eventID.value }),
          );

          return getStandardFilterOperation(FamilyRegistration, subscribePaginatedAndFiltered)(
            skip,
            limit,
            changedFilter,
          );
        },
        onViewChanged: async view => {
          if (!this._config.value) return;
          this._config.value.view = view;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
        isColumnResizable: true,
        isColumnSelectable: true,
        isColumnResortable: true,
        isEntryClickable: false,
        fieldSelectionOptions: {
          hideFieldsWithoutTranslation: true,
          shouldIncludeField: shouldIncludeFieldHelpers.bySelectionSet({ blacklist: { eventID: 1, event: 1 } }),
        },
        onlyShowLastTranslation: true,
        customColumns: [
          {
            fieldName: 'vorname',
            getFieldMapping: e => {
              return e.vorname || html`<cr-text ${css({ opacity: 0.3 })}>keine Angabe...</cr-text>`;
            },
          },
          {
            fieldName: 'nachname',
            getFieldMapping: e => {
              return e.nachname || html`<cr-text ${css({ opacity: 0.3 })}>keine Angabe...</cr-text>`;
            },
          },
          {
            fieldName: 'email',
            getFieldMapping: e => {
              return e.email || html`<cr-text ${css({ opacity: 0.3 })}>keine Angabe...</cr-text>`;
            },
          },
          {
            fieldName: 'sprachbedarf',
            getFieldMapping: e => {
              return e.sprachbedarf || html`<cr-text ${css({ opacity: 0.3 })}>keine Angabe...</cr-text>`;
            },
          },
        ],
      } as DataViewOptions<typeof FamilyRegistration>,
      {
        overridesForFieldName: {
          status: ListAspectSchema(['accepted', 'declined'], 'Status'),
        },
        fieldAspectGeneration: { hideFieldsWithoutTranslation: true, blacklist: { eventID: 1, event: 1 } },
        onFilterChanged: async filter => {
          if (!this._config.value?.view) return;
          this._config.value.view.filter = filter;
          if (this._config.value._id) await this._config.value.save();
          else await this._config.value.create();
        },
      },
    );

    return html`
      <cr-flex space="md">
        <cr-card padding="sm"> ${FilterEditor({ options: options.filterOptions })} </cr-card>
        <cr-card padding="md">
          <cr-flex ${css({ overflow: 'auto' })}>
            <cr-data-view ${css({ marginTop: this.spacing.sm })} .options=${options.dataViewOptions}> </cr-data-view>
          </cr-flex>
        </cr-card>
      </cr-flex>
    `;
  }
}
